import { deleteCookie } from 'cookies-next/client';
import type { CookieStorageItems } from '@/core/models/cookie-storage';
import { ClientCookies } from '@/core/services/cookie-storage';

export const CHECKOUT_MARKET_COUNTRY_STORAGE_KEY =
  'mindoasisCheckoutMarketCountry';

export const FRESH_ORDER_ID_STORAGE_KEY = 'mindoasisFreshOrderId';

const CHECKOUT_COOKIE_NAMES = [
  'orderId',
  'adyenSession',
  'cartItemsCount',
] as const satisfies ReadonlyArray<keyof CookieStorageItems>;

export type DropCartForMarketSwitchParams = {
  orderId: string | null | undefined;
  organizationCountryId: string | null | undefined;
  storedMarketCountry: string | null;
  shippingAddressCountryCode?: string | null;
  billingAddressCountryCode?: string | null;
  /** When true, do not drop solely because profile-prefilled addresses differ from org. */
  hasLineItems?: boolean;
};

/**
 * True when an existing cart belongs to a different market than the current org.
 * Covers stale sessionStorage marker (null) with wrong-country cart addresses on ACC.
 */
export function shouldDropCartForMarketSwitch(
  params: DropCartForMarketSwitchParams,
): boolean {
  if (!params.orderId) return false;

  const organizationCountry = (params.organizationCountryId ?? '')
    .trim()
    .toUpperCase();
  if (!organizationCountry) return false;

  const storedMarketCountry = params.storedMarketCountry?.trim().toUpperCase();
  if (storedMarketCountry && storedMarketCountry !== organizationCountry) {
    return true;
  }

  const shippingCountry = (params.shippingAddressCountryCode ?? '')
    .trim()
    .toUpperCase();
  const billingCountry = (params.billingAddressCountryCode ?? '')
    .trim()
    .toUpperCase();

  const hasAddressMarketMismatch =
    (shippingCountry && shippingCountry !== organizationCountry) ||
    (billingCountry && billingCountry !== organizationCountry);

  if (!hasAddressMarketMismatch) return false;

  // Active cart with items: checkout will rewrite addresses for the current org.
  if (params.hasLineItems) return false;

  // New empty cart: BFF may prefill profile addresses from another market (e.g. NL on en-no).
  if (params.orderId === getFreshOrderId()) return false;

  return true;
}

/** Clears cart/payment cookies and checkout sessionStorage (client-only). */
export function clearCheckoutMarketSession(): void {
  if (typeof window === 'undefined') return;

  for (const name of CHECKOUT_COOKIE_NAMES) {
    deleteCookie(name);
    void ClientCookies.remove(name);
    document.cookie = `${name}=; Max-Age=0; path=/`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
  }

  sessionStorage.removeItem('checkoutCartSnapshot');
  sessionStorage.removeItem('checkoutRedirectInProgress');
  sessionStorage.removeItem('adyenProcessedRedirect');
  sessionStorage.removeItem(CHECKOUT_MARKET_COUNTRY_STORAGE_KEY);
  sessionStorage.removeItem(FRESH_ORDER_ID_STORAGE_KEY);
}

export function markFreshOrderId(orderId: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(FRESH_ORDER_ID_STORAGE_KEY, orderId);
}

export function getFreshOrderId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(FRESH_ORDER_ID_STORAGE_KEY);
}

export function clearFreshOrderId(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(FRESH_ORDER_ID_STORAGE_KEY);
}

export function getStoredCheckoutMarketCountry(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(CHECKOUT_MARKET_COUNTRY_STORAGE_KEY);
}

export function setStoredCheckoutMarketCountry(countryCode: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(
    CHECKOUT_MARKET_COUNTRY_STORAGE_KEY,
    countryCode.trim().toUpperCase(),
  );
}
